import React, { useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { SortableContext } from '@dnd-kit/sortable';
import { GripVertical } from 'lucide-react';
import { DISCOUNT_CODE_ITEM_ID, ROOT_CONTAINER, findLayoutItem, layoutItemKey } from '@repo/lib';
import FieldItem from './FieldItem';
import GroupCard from './GroupCard';
import HeadingItem from './HeadingItem';
import DividerItem from './DividerItem';
import DiscountCodeItem from './DiscountCodeItem';
import QuickAddFields from './QuickAddFields';
import AddPickerDialog from './AddPicker/AddPickerDialog';
import PickerMenuItems from './AddPicker/PickerMenuItems';
import { PICKER_MENU_CLASS, pickerSections } from './AddPicker/pickerItems';
import SortableBlock from './SortableBlock';
import { useFieldEdit } from '../FieldEditContext';
import { useFields } from '../FieldsContext';
import { groupDisplayName, useFormLayout } from '../FormLayoutContext';
import { toast } from '@dashboard/context/ToastContext';
import { useFieldSourceSettings } from '../FieldSourceContext';
import { getElementFieldTypes } from '@dashboard/services/pageFieldTypes';
import { applyDropTarget, dropStops, dropTargetAt, staticStrategy } from './fieldsDrag';
import Button from '@dashboard/components/Button';
import { Accordion } from '@dashboard/components/ui/accordion';
import usePage from '@dashboard/hooks/usePage';
import { useIsMobile } from '@dashboard/hooks/use-mobile';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@dashboard/components/ui/dropdown-menu';

const sameTarget = (a, b) =>
  a === b ||
  (a && b && a.containerId === b.containerId && a.beforeKey === b.beforeKey && a.into === b.into);

export default function FieldsList() {
  const { isAddingField, onAddField, isReordering, setIsEditingField } = useFieldEdit();
  const {
    displayLayout,
    toItems,
    saveLayout,
    setDraftGroup,
    fieldsById,
    addGroup,
    addHeading,
    addDivider,
    addDiscountCode,
    hasDiscountCode,
  } = useFormLayout();
  const { page } = usePage();
  const isMobile = useIsMobile();
  const { state: fields } = useFields();
  const [openBlock, setOpenBlock] = useState('');
  // Where the open picker adds: `{ groupId }`, with null for the form itself.
  const [picker, setPicker] = useState(null);
  const [drag, setDrag] = useState(null);
  // The cached layout updates a tick after the drop, so the dropped order is held until the save
  // settles; without it the list flashes its old order for a frame.
  const [dropped, setDropped] = useState(null);

  const [target, setTarget] = useState(null);
  const targetRef = useRef(null);
  const listRef = useRef(null);
  const pointer = useRef(null);
  const keyboard = useRef(null);

  const rectOf = key =>
    listRef.current
      ?.querySelector(`[data-sortable-id="${CSS.escape(key)}"]`)
      ?.getBoundingClientRect();

  const showTarget = next => {
    if (sameTarget(next, targetRef.current)) return;
    targetRef.current = next;
    setTarget(next);
  };

  const keyboardCoordinates = (event, { context, currentCoordinates }) => {
    const step = { ArrowDown: 1, ArrowUp: -1 }[event.code];
    const state = keyboard.current;
    if (!step || !state) return undefined;
    event.preventDefault();
    state.index = Math.max(0, Math.min(state.stops.length - 1, state.index + step));
    const stop = state.stops[state.index];
    showTarget(state.index === state.current ? null : stop);

    const list =
      stop.containerId === ROOT_CONTAINER
        ? state.layout
        : (state.layout.find(item => item.id === stop.containerId)?.items ?? []);
    const anchor = stop.into
      ? rectOf(`group:${stop.containerId}`)
      : rectOf(stop.beforeKey ?? layoutItemKey(list.at(-1)));
    if (!anchor) return currentCoordinates;
    const y = stop.into
      ? (anchor.top + anchor.bottom) / 2
      : stop.beforeKey
        ? anchor.top
        : anchor.bottom;
    return { x: currentCoordinates.x, y: y - (context.collisionRect?.height ?? 0) / 2 };
  };

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: keyboardCoordinates }),
  );

  const viewLayout = drag?.layout ?? dropped ?? displayLayout;
  const items = useMemo(() => toItems(viewLayout), [toItems, viewLayout]);

  const openField = useMemo(
    () => [...fieldsById.values()].find(field => field.isEditing),
    [fieldsById],
  );
  const openValue = openField ? openField._id || openField.key : openBlock;
  const locked = isAddingField || isReordering;

  const handleValueChange = value => {
    const isBlock = value && /^(group|heading|discount-code):/.test(value);
    if (openField && (isBlock || !value)) setIsEditingField(false, openField._id || openField.key);
    if (isBlock) setOpenBlock(value);
    else {
      setOpenBlock('');
      if (value) setIsEditingField(true, value);
    }
  };

  const onAddToGroup = groupId => setPicker({ groupId });
  const { allowedElements: sourceElements, hasLayout, layoutBlocks } = useFieldSourceSettings();
  const allowedElements = sourceElements ?? getElementFieldTypes(page.type);
  const guestQuestions = !hasLayout;
  const discountCode =
    page.type === 'form' || guestQuestions ? null : { placed: hasDiscountCode };
  const sectionsFor = groupId =>
    pickerSections({
      groupId,
      allowedElements,
      fields,
      discountCode,
      guestQuestions,
      layoutBlocks,
    });

  const onPicked = async (item, groupId = picker?.groupId ?? null) => {
    setPicker(null);
    if (item.kind === 'field') {
      const key = onAddField(item.element, item.preset);
      if (key && groupId) setDraftGroup(key, groupId);
    } else if (item.kind === 'discount-code') {
      if (!(await addDiscountCode(groupId))) return;
      setOpenBlock(`discount-code:${DISCOUNT_CODE_ITEM_ID}`);
      toast({
        title: 'Discount code added',
        description: groupId ? `In ${groupDisplayName(displayLayout, groupId)}` : undefined,
        type: 'success',
      });
    } else if (item.kind === 'divider') {
      if (!(await addDivider(groupId))) return;
      toast({
        title: 'Divider added',
        description: groupId ? `In ${groupDisplayName(displayLayout, groupId)}` : undefined,
        type: 'success',
      });
    } else if (item.kind === 'heading') {
      const id = await addHeading(item.level, groupId);
      if (!id) return;
      setOpenBlock(`heading:${id}`);
      toast({
        title: `${item.label} added`,
        description: groupId ? `In ${groupDisplayName(displayLayout, groupId)}` : undefined,
        type: 'success',
      });
    } else if (await addGroup()) {
      toast({ title: 'Group added', type: 'success' });
    }
  };

  const stopPointer = () => {
    window.removeEventListener('pointermove', pointer.current?.listener);
    pointer.current = null;
  };

  const onDragStart = ({ active, activatorEvent }) => {
    const layout = dropped ?? displayLayout;
    const activeKey = String(active.id);
    showTarget(null);
    keyboard.current = null;
    stopPointer();
    if (activatorEvent && 'clientY' in activatorEvent) {
      const listener = event => {
        pointer.current.y = event.clientY;
      };
      pointer.current = { y: activatorEvent.clientY, listener };
      window.addEventListener('pointermove', listener);
    } else {
      keyboard.current = { layout, ...dropStops(layout, activeKey) };
      keyboard.current.index = keyboard.current.current;
    }
    setDrag({ activeKey, layout });
  };

  const onDragMove = () => {
    if (!drag || !pointer.current) return;
    showTarget(dropTargetAt(drag.layout, drag.activeKey, pointer.current.y, rectOf));
  };

  const endDrag = () => {
    const current = drag;
    const finalTarget = targetRef.current;
    stopPointer();
    keyboard.current = null;
    showTarget(null);
    setDrag(null);
    return current && finalTarget ? { current, finalTarget } : null;
  };

  const onDragEnd = () => {
    const ended = endDrag();
    if (!ended) return;
    const finalLayout = applyDropTarget(
      ended.current.layout,
      ended.current.activeKey,
      ended.finalTarget,
    );
    if (JSON.stringify(finalLayout) === JSON.stringify(displayLayout)) return;
    setDropped(finalLayout);
    saveLayout(finalLayout).finally(() => setDropped(null));
  };

  const dropLineFor = (key, containerId, list) => {
    if (!target || target.into || target.containerId !== containerId) return null;
    if (target.beforeKey === key) return 'before';
    return !target.beforeKey && layoutItemKey(list.at(-1)) === key ? 'after' : null;
  };

  const draggingItem = drag && findLayoutItem(drag.layout, drag.activeKey)?.item;
  const dragLabel = !draggingItem
    ? ''
    : draggingItem.type === 'field'
      ? fieldsById.get(draggingItem.fieldId)?.label || 'Field'
      : draggingItem.type === 'heading'
        ? draggingItem.text
        : draggingItem.type === 'divider'
          ? 'Divider'
          : draggingItem.type === 'discount-code'
            ? 'Discount code'
            : groupDisplayName(drag.layout, draggingItem.id);

  const renderBlock = (item, containerId, afterGroup = false) => {
    const key = layoutItemKey(item);
    if (item.type === 'field' && !item.field) return null;
    const isDraft = item.type === 'field' && !item.field._id;
    const list =
      containerId === ROOT_CONTAINER
        ? viewLayout
        : (viewLayout.find(entry => entry.id === containerId)?.items ?? []);
    return (
      <SortableBlock
        key={key}
        id={key}
        data={{ containerId }}
        disabled={locked || isDraft}
        border={item.type !== 'group'}
        borderTop={afterGroup && item.type !== 'group'}
        dropLine={dropLineFor(key, containerId, list)}
      >
        {({ dragHandleProps }) => {
          if (item.type === 'field') {
            return (
              <FieldItem
                field={item.field}
                disabled={isReordering}
                dragHandleProps={dragHandleProps}
                containerId={containerId}
              />
            );
          }
          if (item.type === 'discount-code') {
            return (
              <DiscountCodeItem
                item={item}
                dragHandleProps={dragHandleProps}
                containerId={containerId}
                disabled={locked}
              />
            );
          }
          if (item.type === 'divider') {
            return (
              <DividerItem
                divider={item}
                dragHandleProps={dragHandleProps}
                containerId={containerId}
                disabled={locked}
              />
            );
          }
          if (item.type === 'heading') {
            return (
              <HeadingItem
                heading={item}
                dragHandleProps={dragHandleProps}
                containerId={containerId}
                disabled={locked}
                onClose={() => setOpenBlock('')}
              />
            );
          }
          return (
            <GroupCard
              group={item}
              dragHandleProps={dragHandleProps}
              isDropTarget={target?.containerId === item.id}
              into={Boolean(target?.into) && target.containerId === item.id}
              disabled={locked}
              onAddToGroup={onAddToGroup}
              addMenu={
                isMobile && {
                  sections: sectionsFor(item.id),
                  onPick: picked => onPicked(picked, item.id),
                }
              }
              onClose={() => setOpenBlock('')}
              renderChild={renderBlock}
            />
          );
        }}
      </SortableBlock>
    );
  };

  return (
    <div>
      {items.length > 0 && (
        <div className="mb-4" ref={listRef}>
          <Accordion
            type="single"
            collapsible
            value={openValue}
            onValueChange={handleValueChange}
            className="border-t"
          >
            <DndContext
              sensors={sensors}
              collisionDetection={() => []}
              onDragStart={onDragStart}
              onDragMove={onDragMove}
              onDragEnd={onDragEnd}
              onDragCancel={endDrag}
            >
              <SortableContext
                id={ROOT_CONTAINER}
                items={viewLayout.map(layoutItemKey)}
                strategy={staticStrategy}
              >
                {items.map((item, index) =>
                  renderBlock(item, ROOT_CONTAINER, items[index - 1]?.type === 'group'),
                )}
              </SortableContext>
              <DragOverlay dropAnimation={null}>
                {drag && (
                  <div className="flex h-12 items-center gap-2 rounded-md border bg-white px-2 text-sm shadow-lg dark:bg-background">
                    <GripVertical className="size-4 text-muted-foreground" />
                    <span className="truncate">{dragLabel}</span>
                  </div>
                )}
              </DragOverlay>
            </DndContext>
          </Accordion>
        </div>
      )}

      {/* Hidden, not removed, while a field, heading or group is open, so Quick add stays put. */}
      <div
        className={clsx('flex justify-end', { invisible: openField || openBlock || isAddingField })}
      >
        {isMobile ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild disabled={locked}>
              <Button disabled={locked} appearance="primary">
                + Add
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className={PICKER_MENU_CLASS}>
              <PickerMenuItems sections={sectionsFor(null)} onPick={item => onPicked(item, null)} />
            </DropdownMenuContent>
          </DropdownMenu>
        ) : (
          <Button
            disabled={locked}
            handleClick={() => setPicker({ groupId: null })}
            appearance="primary"
          >
            + Add
          </Button>
        )}
      </div>

      <QuickAddFields />

      {picker && (
        <AddPickerDialog
          open
          onOpenChange={open => !open && setPicker(null)}
          onConfirm={onPicked}
          groupId={picker.groupId}
          allowedElements={allowedElements}
          fields={fields}
          discountCode={discountCode}
          guestQuestions={guestQuestions}
          layoutBlocks={layoutBlocks}
        />
      )}
    </div>
  );
}
